import type { MutableRefObject, RefCallback } from 'react';
import type { XstyledProps, DSPropTypesSchema, ValidationMap } from '@elliemae/ds-props-helpers';
import {
  PropTypes,
  globalAttributesPropTypes,
  xstyledPropTypes,
  getPropsPerSlotPropTypes,
} from '@elliemae/ds-props-helpers';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { BANNER_TYPES, DSBannerName, DSBannerSlots } from './exported-related/index.js';

export declare namespace DSBannerT {
  export interface ActionRef {
    focusOnWrapper?: () => void;
    focusOnLink?: () => void;
  }

  export interface ActionLinkT {
    label: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void | null;
    href?: string;
  }

  export type BannerTypesT = (typeof BANNER_TYPES)[keyof typeof BANNER_TYPES];

  export interface RequiredProps {}

  export interface DefaultProps {
    containerProps: Record<string, unknown>;
    label: string;
    body: string;
    type: BannerTypesT;
    isOpen: boolean;
    onClose: () => void | null;
    showCloseButton: boolean;
    withLiveRegionAnnouncement: 'alert' | 'assertive' | 'polite' | 'none';
    isPageNotice: boolean;
  }

  export interface OptionalProps
    extends TypescriptHelpersT.PropsForGlobalOnSlots<typeof DSBannerName, typeof DSBannerSlots> {
    actionLink?: ActionLinkT;
    CustomBody?: React.ComponentType<Record<string, unknown>>;
    closeBtnRef?: React.MutableRefObject<HTMLButtonElement | null> | RefCallback<HTMLButtonElement>;
    actionRef?: MutableRefObject<ActionRef>;
  }
  export interface Props extends Partial<DefaultProps>, OptionalProps, XstyledProps, RequiredProps {}

  export interface InternalProps extends DefaultProps, OptionalProps, XstyledProps, RequiredProps {}
}

export const defaultProps: DSBannerT.DefaultProps = {
  containerProps: {},
  label: '',
  body: '',
  type: BANNER_TYPES.INFO,
  isOpen: true,
  onClose: () => null,
  showCloseButton: true,
  withLiveRegionAnnouncement: 'alert',
  isPageNotice: false,
};

export const DSBannerPropTypes: DSPropTypesSchema<DSBannerT.Props> = {
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  ...getPropsPerSlotPropTypes(DSBannerName, DSBannerSlots),
  containerProps: PropTypes.object.description('Set of Properties attached to the main container.').defaultValue({}),
  label: PropTypes.string.description('Banner label.').defaultValue(''),
  body: PropTypes.string.description('Banner body content').defaultValue(''),
  CustomBody: PropTypes.func.description('Banner custom content').defaultValue(''),
  type: PropTypes.oneOf(Object.values(BANNER_TYPES)).description('Banner type.').defaultValue(BANNER_TYPES.INFO),
  onClose: PropTypes.func.description('Callback when the Banner closes.'),
  isOpen: PropTypes.bool.description('Whether the Banner is open or closed.').defaultValue(true),
  showCloseButton: PropTypes.bool.description('Whether to show close button or not.').defaultValue(true),
  closeBtnRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).description('Inner ref to button component.'),
  actionLink: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    href: PropTypes.string,
  })
    .description('Properties for the Action Link.')
    .defaultValue({}),
  actionRef: PropTypes.object
    .description(
      `
      Reference to use actions:
      - focusOnLink: function to focus the banner
      - focusOnWrapper: function to focus the link
    `,
    )
    .defaultValue({}),
  withLiveRegionAnnouncement: PropTypes.oneOf(['alert', 'assertive', 'polite'])
    .description('Whether to announce the banner content in a live region when it opens.')
    .defaultValue('alert'),
  isPageNotice: PropTypes.bool.description('Whether to add role region to the banner.').defaultValue(false),
};

export const DSBannerPropTypesSchema = DSBannerPropTypes as unknown as ValidationMap<DSBannerT.Props>;
